import { z } from 'zod'

// Unified permission vocabulary. Used in two places:
//   1. The `permissions` set granted by a Role (users carry permissions via
//      their assigned role row in the `roles` table).
//   2. The `scopes` array stamped onto an application API token at mint time.
//
// Format: `<resource>:<verb>` for fine-grained gates, plus the master
// `admin` switch for full /admin/* access. For data resources, add both
// read + write even if only one is initially needed — keeps the matrix
// uniform. UI-only surface gates (a page/section with nothing to write,
// e.g. `api:read`) are the exception: a lone `:read` is enough.
//
// This is the template's starter vocabulary. The `users:*` gates back the
// admin Users page (UI + API); `api:read` / `mcp:read` / `guide:read` are
// UI-visibility gates for their sections; `admin` is the master switch. To
// add a resource (say, `notes`), add `notes:read` / `notes:write` here and
// map them onto roles in SEEDED_ROLE_PERMISSIONS (./role.ts) — see CLAUDE.md
// "Adding an entity".
export const permissionSchema = z.enum([
  'users:read',
  'users:write',
  // Developer surfaces — gate the API reference and MCP instructions pages
  // (and their sidebar links). Held by no seeded role except via `admin`;
  // grant them per-role in the admin Roles editor.
  'api:read',
  'mcp:read',
  // Gates the in-app guide section (pages + sidebar link). Same model as the
  // developer surfaces: grant per-role in the Roles editor; `admin` implies it.
  'guide:read',
  // Gates the dispatch feature: reads (home-page search + shareable session
  // views) and writes (creating sessions, recording call/accept outcomes).
  // Seeded roles grant Viewer `dispatch:read` and Editor both; `admin` implies
  // them.
  'dispatch:read',
  'dispatch:write',
  // Gates the analytics dashboard (UI page + sidebar link) and the
  // GET /api/v1/analytics endpoint. UI-visibility + read-only data gate;
  // held by no seeded role except via `admin`. Grant per-role in the admin
  // Roles editor.
  'analytics:read',
  // Gates POST /api/v1/eta/jobs, which answers Argus's live board with ETAs
  // for a list of job numbers. Meant for an application API key; held by no
  // seeded role except via `admin`.
  'eta:read',
  // Master switch — grants access to /admin/* endpoints and implies every
  // other permission. Soft lockout: at least one user must hold this at all
  // times (enforced by the auth guard in @rapid-response/api, not the schema).
  'admin',
])
export type Permission = z.infer<typeof permissionSchema>

// Application API tokens carry `Scope[]`; the vocabulary is unified with the
// user-facing permission set above. `Scope` is re-exported (rather than a
// plain `type Scope = Permission` alias) so Sonar's redundant-type-aliases
// rule doesn't flag it.
export const scopeSchema = permissionSchema
export { type Permission as Scope }
